import React, { useEffect, useState, useContext } from "react";
import { useNavigate, Routes, Route } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import {
  User,
  LogOut,
  Bell,
  Settings,
  FileText,
  Home,
  Sun,
  Moon,
  DollarSign,
  Clock,
  Coffee,
  Shield,
  BarChart3,
  AlertTriangle,
  UsersRound
} from "lucide-react"; 

// Import page components
import ProfessionalProfile from "./(User)Profile_Final";
import Documents_Page from "./(Employee)SelfService";
import Settings_Page from "./settings_Final";
import Leave_Page from "./(User)leave_Managment";
import Attendance_Page from "./(User)attendance_page";
import Mo3tiSalaryPage from "./(User)salary_Final";
import EscalationPage from "./(User)EscalationComplaints";

import Mo3tiLeavePage from "./(User)leave_Managment";
import Calendar from './Calendar.js';
import { mo3tiThemes } from "./mo3tiThemes";
import SidebarWithTopbar from "./sidebar3.js";
import ViewTypeSwitcher from '../components/ViewTypeSwitcher';
import GroupsManagement from "./GroupsManagement_Final";
// ============ الترجمات الكاملة ============
import { translations_admin } from "../constant/(Admin)Translations.js";


const Mo3tiUserProfile = () => {
  // ============ HOOKS AND STATE ============
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  const isRTL = language === 'ar';
  const t = translations_admin[language];

  // State management
  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // View type state - admin or manager
  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem('viewType') || 'manager';
  });

  // حفظ الصفحة الحالية
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('adminUserProfilePage');
    return savedPage || 'professionalProfile';
  });

  useEffect(() => {
    localStorage.setItem('adminUserProfilePage', currentPage);
  }, [currentPage]);

  const handleViewTypeChange = (type) => {
    setViewType(type);
    // storage of viewType is centralized in ViewTypeSwitcher

    // Navigation logic based on view type
    if (type === 'user') {
      // Check if user has admin level - if yes, stay on userprofileadmin, otherwise go to userprofilemanager
      if (hasAdminLevel()) {
        // Stay on current page (userprofileadmin)
        setCurrentPage('professionalProfile');
      } else {
        navigate('/userprofilemanager/professionalProfile');
      }
    } else if (type === 'manager') {
      navigate('/dashboard/dashboard');
    } else if (type === 'admin') {
      navigate('/dashboard/dashboard');
    }
  };

  // Check if user has an admin level assigned
  const hasAdminLevel = () => {
    try {
      const adminLevelData = localStorage.getItem('adminLevel');
      if (adminLevelData) {
        const adminLevel = JSON.parse(adminLevelData);
        return adminLevel && (adminLevel.modules?.length > 0 || adminLevel.adminLevelId);
      }
    } catch (e) {
      console.error('Error checking admin level:', e);
    }
    return false;
  };

  // ============ HELPER FUNCTIONS ============
  const createAvatar = (name) => {
    if (!name) return "UN";
    const parts = name.split(" ");
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  };

  // ============ DATA CONFIGURATION ============
  const currentTheme = isDarkMode ? mo3tiThemes.dark : mo3tiThemes.light;

  // Navigation menu items - مع الترجمات
  const navItems = [
    {
      icon: User,
      text: t.profile,
      page: "professionalProfile"
    },
    {
      icon: Clock,
      text: t.attendance,
      page: "attendance",
    },
    {
      icon: FileText,
      text: t.employeeSelfServices,
      page: "employee-services",
    },
    {
      icon: Coffee,
      text: t.leaveManagement,
      page: "leave-management",
    },
    {
      icon: DollarSign,
      text: t.salaryBenefits,
      page: "salary",
    },
    {
      icon: Home,
      text: t.calendar,
      page: "calendar",
    },
    {
      icon: AlertTriangle,
      text: t.escalation,
      page: "escalation",
    },
    {
      icon: UsersRound,
      text: t.groups,
      page: "groups",
    },
    {
      icon: Settings,
      text: t.settings,
      page: "settings",
    },
  ];

  // Employee data — profile (from /users/me) is source of truth;
  // localStorage serves as an immediate fallback (populated at login).
  const _phone     = profile?.employeePhone         || localStorage.getItem("employeePhone")         || "";
  const _emergency = profile?.emergencyPhoneNumber  || localStorage.getItem("emergencyPhoneNumber")  || "";
  const _name      = profile?.employeeName          || localStorage.getItem("employeeName")          || t.unknownEmployee;
  const employeeData = {
    id:               profile?.infinityCode                  || localStorage.getItem("infinityCode")                  || "EMP001",
    name:             _name,
    position:         profile?.employeeType                  || localStorage.getItem("employeeType")                  || "--",
    department:       profile?.employeeType                  || localStorage.getItem("employeeType")                  || "--",
    email:            profile?.employeeEmail                 || localStorage.getItem("employeeEmail")                 || localStorage.getItem("email") || "employee@company.com",
    phone:            _phone ? "+20" + _phone : "--",
    address:          profile?.address                       || localStorage.getItem("address")                       || localStorage.getItem("location") || "--",
    joinDate:         profile?.hiringDate                    || localStorage.getItem("hiringDate")                    || null,
    employeeId:       profile?.infinityCode                  || localStorage.getItem("infinityCode")                  || "EMP001",
    manager:          profile?.firstLineManagerName          || localStorage.getItem("firstLineManagerName")          || "--",
    status:           profile?.employeeState                 || localStorage.getItem("employeeState")                 || t.activeStatus,
    avatar:           createAvatar(_name),
    birthDate:        profile?.DoB                           || localStorage.getItem("DoB")                           || null,
    nationalId:       profile?.nationalId                    || localStorage.getItem("nationalId")                    || "--",
    emergencyContact: _emergency ? "+20" + _emergency : "--",
    workLocation:     profile?.location                      || localStorage.getItem("location")                      || "--",
    firstLevelManager: profile?.firstLineManagerInfinityCode || localStorage.getItem("firstLineManagerInfinityCode") || "--",
  };

  // Salary data — only real value from profile; rest fetched by usePayrollData hook in child
  const salaryData = {
    netSalary: parseFloat(profile?.netSalary || localStorage.getItem("netSalary") || "0") || 0,
  };

  // ============ EVENT HANDLERS ============
  const handleLogout = () => {
    try {
      logout();
      try {
        localStorage.removeItem('nav_admin');
        localStorage.removeItem('nav_manager');
        localStorage.removeItem('nav_employee');
      } catch (e) { }
      navigate("/login");
    } catch (error) {
      console.error(t.logoutError, error);
      navigate("/login");
    }
  };

  const handleSwitchManager = () => {
    try {
      localStorage.removeItem('adminUserProfilePage');
      navigate("/dashboard/dashboard");
    } catch (error) {
      console.error(t.switchError, error);
      navigate("/userprofileadmin");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate(`/userprofileadmin/${page}`);
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (setTheme) {
      setTheme(newDarkMode ? "dark" : "light");
    }
  };

  // ============ CREATE PAGE PROPS ============
  const pageProps = {
    employeeData,
    salaryData,
    theme: currentTheme,
    isDarkMode,
    translations_admin: t,
    isRTL
  };

  // ============ RENDER THEME TOGGLE ============
  const renderThemeToggle = () => (
    <button
      style={{
        padding: "8px",
        borderRadius: "12px",
        cursor: "pointer",
        color: theme === "light" ? "#0f172a" : "white",
        background: theme === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease"
      }}
      onClick={toggleTheme}
      title={theme === "light" ? t.toggleDarkMode : t.toggleLightMode}
      aria-label={theme === "light" ? t.toggleDarkMode : t.toggleLightMode}
    >
      {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
    </button>
  );

  // ============ STYLES ============
  const containerStyles = {
    minHeight: "100vh",
    background: currentTheme?.background?.gradient || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    fontFamily: isRTL
      ? "'Cairo', 'Tajawal', " + (currentTheme?.fonts?.primary || "Arial, sans-serif")
      : currentTheme?.fonts?.primary || "Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
    direction: isRTL ? "rtl" : "ltr",
  };

  const backgroundOverlayStyles = currentTheme?.background?.overlay || {};
  const decorativeContainerStyles = currentTheme?.background?.decorative?.container || {};
  const circle1Styles = {
    ...(currentTheme?.background?.decorative?.circle1 || {}),
    [isRTL ? 'left' : 'right']: currentTheme?.background?.decorative?.circle1?.right || '0',
  };
  const circle2Styles = {
    ...(currentTheme?.background?.decorative?.circle2 || {}),
    [isRTL ? 'right' : 'left']: currentTheme?.background?.decorative?.circle2?.left || '0',
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&family=Tajawal:wght@400;500;700;800&display=swap');
    
    * { 
      box-sizing: border-box;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    
    *::-webkit-scrollbar {
      display: none;
    }
    
    body {
      margin: 0;
      padding: 0;
      font-family: ${isRTL ? "'Cairo', 'Tajawal', " : ""}${currentTheme?.fonts?.primary || "Arial, sans-serif"};
      direction: ${isRTL ? 'rtl' : 'ltr'};
      overflow-x: hidden;
    }
    
    input, textarea, select {
      font-family: inherit;
      direction: ${isRTL ? 'rtl' : 'ltr'};
      text-align: ${isRTL ? 'right' : 'left'};
    }
    
    input::placeholder { 
      color: ${currentTheme?.colors?.textSecondary || "#666"}; 
      text-align: ${isRTL ? 'right' : 'left'};
    }
    
    /* RTL Support - دعم كامل للسايد بار */
    .mo3ti-sidebar {
      position: fixed;
      top: 0;
      ${isRTL ? 'right: 0; left: auto;' : 'left: 0; right: auto;'}
      height: 100vh;
      width: 280px;
      transition: all 0.3s ease;
      z-index: 100;
    }
    
    .mo3ti-main {
      ${isRTL ? 'margin-right: 280px; margin-left: 0;' : 'margin-left: 280px; margin-right: 0;'}
      transition: all 0.3s ease;
      min-height: 100vh;
    }
    
    .mo3ti-content {
      direction: ${isRTL ? 'rtl' : 'ltr'};
      text-align: ${isRTL ? 'right' : 'left'};
    }
    
    /* تحسين عرض النصوص والأيقونات في السايد بار */
    .mo3ti-sidebar .nav-item {
      display: flex;
      align-items: center;
      ${isRTL ? 'flex-direction: row-reverse;' : 'flex-direction: row;'}
      justify-content: ${isRTL ? 'flex-end' : 'flex-start'};
      gap: 12px;
      padding: 12px 16px;
      border-radius: 12px;
      transition: all 0.3s ease;
      text-align: ${isRTL ? 'right' : 'left'};
    }
    
    .mo3ti-sidebar .nav-item-text {
      flex: 1;
      text-align: ${isRTL ? 'right' : 'left'};
      direction: ${isRTL ? 'rtl' : 'ltr'};
    }
    
    .mo3ti-sidebar .nav-icon {
      flex-shrink: 0;
    }
    
    /* تحسين التوب بار */
    .mo3ti-topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      ${isRTL ? 'flex-direction: row-reverse;' : 'flex-direction: row;'}
    }
    
    .mo3ti-topbar-actions {
      display: flex;
      align-items: center;
      ${isRTL ? 'flex-direction: row-reverse;' : 'flex-direction: row;'}
      gap: 12px;
    }
    
    .mo3ti-switch-button {
      display: flex;
      align-items: center;
      ${isRTL ? 'flex-direction: row-reverse;' : 'flex-direction: row;'}
      gap: 8px;
      padding: 8px 16px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    
    .mo3ti-user-info {
      display: flex;
      align-items: center;
      ${isRTL ? 'flex-direction: row-reverse;' : 'flex-direction: row;'}
      gap: 12px;
    }
    
    .mo3ti-user-details {
      text-align: ${isRTL ? 'right' : 'left'};
      direction: ${isRTL ? 'rtl' : 'ltr'};
    }
    
    /* Responsive Design */
    @media (max-width: 768px) {
      .mo3ti-sidebar { 
        width: 100% !important; 
        ${isRTL ? 'right' : 'left'}: ${sidebarOpen ? "0" : "-100%"} !important; 
        ${isRTL ? 'left' : 'right'}: auto !important;
        z-index: 1000;
      }
      
      .mo3ti-main { 
        ${isRTL ? 'margin-right' : 'margin-left'}: 0 !important; 
        ${isRTL ? 'margin-left' : 'margin-right'}: 0 !important;
      }
      
      .mo3ti-topbar { 
        padding: 12px 16px !important; 
      }
      
      .mo3ti-content { 
        padding: 20px 16px !important; 
      }
      
      .mo3ti-search { 
        width: 200px !important; 
      }
    }
    
    @media (max-width: 480px) {
      .mo3ti-content { 
        padding: 16px 12px !important; 
      }
      
      .mo3ti-search { 
        width: 150px !important; 
      }
    }
    
    /* Animations */
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .fade-in {
      animation: fadeIn 0.3s ease-in-out;
    }
    
    /* Card Styles */
    .mo3ti-card {
      background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.95)'};
      backdrop-filter: blur(10px);
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 20px;
      border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
      transition: all 0.3s ease;
      direction: ${isRTL ? 'rtl' : 'ltr'};
      text-align: ${isRTL ? 'right' : 'left'};
    }
    
    .mo3ti-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px ${isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'};
    }
    
    /* Icon + Text Wrapper */
    .icon-text-wrapper {
      display: flex;
      align-items: center;
      ${isRTL ? 'flex-direction: row-reverse;' : 'flex-direction: row;'}
      gap: 8px;
    }
    
    /* Button Groups */
    .button-group {
      display: flex;
      ${isRTL ? 'flex-direction: row-reverse;' : 'flex-direction: row;'}
      gap: 12px;
      align-items: center;
    }
  `;

  // ============ MAIN RENDER ============
  return (
    <div style={containerStyles}>
      {/* Background Elements */}
      <div style={backgroundOverlayStyles} />
      <div style={decorativeContainerStyles}>
        <div style={circle1Styles} />
        <div style={circle2Styles} />
      </div>

      {/* Sidebar with Topbar */}
      <SidebarWithTopbar
        navItems={navItems}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        user={user}
        notifications={notifications}
        handleLogout={handleLogout}
        handleSwitchManager={handleSwitchManager}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        themeToggle={renderThemeToggle()}
        translations_admin={t}
        isRTL={isRTL}
        viewModeSwitcher={
          <ViewTypeSwitcher
            viewType={viewType}
            onSelect={(type) => handleViewTypeChange(type)}
            hasAdminLevel={() => hasAdminLevel()}
            role={localStorage.getItem('role')}
          />
        }
      >
        <div style={{
          padding: "32px 24px",
          direction: isRTL ? "rtl" : "ltr",
          textAlign: isRTL ? "right" : "left",
        }}>
          <Routes>
            <Route path="/" element={<ProfessionalProfile {...pageProps} />} />
            <Route path="professionalProfile" element={<ProfessionalProfile {...pageProps} />} />
            <Route path="salary" element={<Mo3tiSalaryPage {...pageProps} />} />
            <Route path="attendance" element={<Attendance_Page {...pageProps} />} />
            <Route path="escalation" element={<EscalationPage {...pageProps} />} />
            <Route path="groups" element={<GroupsManagement {...pageProps} />} />
            <Route path="employee-services" element={<Documents_Page {...pageProps} />} />
            <Route path="calendar" element={<Calendar {...pageProps} />} />
            <Route path="leave-management" element={<Leave_Page {...pageProps} />} />
            <Route path="settings" element={<Settings_Page {...pageProps} toggleTheme={toggleTheme} />} />
          </Routes>
        </div>
      </SidebarWithTopbar>

      {/* Global Styles */}
      <style>{globalStyles}</style>
    </div>
  );
};

export default Mo3tiUserProfile;
